import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Plus, ArrowLeft } from 'lucide-react';
import { Turbine } from '../types';
import 'leaflet/dist/leaflet.css';

const PageContainer = styled.div`
  padding: 30px;
  max-width: 1600px;
  margin: 0 auto;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #6b7280;
  
  a {
    color: #0099ff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    color: #1a1f3a;
    font-weight: 600;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  div {
    h1 {
      font-size: 32px;
      font-weight: 700;
      color: #1a1f3a;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #6b7280;
      margin: 0;
    }
  }
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1a1f3a;
  margin-bottom: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 153, 255, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  
  h2 {
    font-size: 16px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    text-align: left;
    padding: 12px;
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
  }
  
  td {
    padding: 12px;
    border-top: 1px solid #f3f4f6;
    color: #1a1f3a;
    font-size: 14px;
  }
  
  tr {
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover {
      background: #f9fafb;
    }
  }
`;

const MapContainer2 = styled.div`
  height: 450px;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 16px;
  
  .leaflet-container {
    height: 100%;
    border-radius: 8px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const Turbines: React.FC = () => {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const [turbines, setTurbines] = useState<Turbine[]>([]);
  const [farmName, setFarmName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTurbines();
  }, [farmId]);

  const loadTurbines = async () => {
    try {
      // TODO: Load from R2 storage
      setTurbines([]);
      setFarmName('Demo Farm'); // Placeholder
    } catch (error) {
      console.error('Failed to load turbines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTurbineClick = (turbineId: string) => {
    navigate(`/turbines/${turbineId}`);
  };

  const handleAddTurbine = () => {
    // TODO: Navigate to file uploader
    navigate('/inspection');
  };

  const defaultCenter: [number, number] = [39.0, 35.0];

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/wind-farms')}>
        <ArrowLeft />
        Back to Wind Farms
      </BackButton>

      <Breadcrumb>
        <a href="/">Dashboard</a> ›
        <a href="/wind-farms">Wind Farms</a> ›
        <span>Turbines</span>
      </Breadcrumb>

      <Header>
        <div>
          <h1>TURBINES</h1>
          <p>List of turbines of the wind farm</p>
        </div>
        <AddButton onClick={handleAddTurbine}>
          <Plus />
          Add Turbine
        </AddButton>
      </Header>

      {turbines.length > 0 ? (
        <>
          <Section>
            <h2>{farmName} Turbines</h2>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
              You can access turbine information by clicking on the turbine name.
            </p>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Turbine Name</th>
                  <th>Model</th>
                  <th>Turbine No</th>
                  <th>Diameter</th>
                </tr>
              </thead>
              <tbody>
                {turbines.map((turbine, index) => (
                  <tr key={turbine.id} onClick={() => handleTurbineClick(turbine.id)}>
                    <td>{index + 1}</td>
                    <td>{turbine.name}</td>
                    <td>{turbine.model}</td>
                    <td>{turbine.turbineNumber}</td>
                    <td>{turbine.rotorDiameter}m</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>

          <Section>
            <h2>{farmName} Turbine Map</h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              You can access the turbine name by clicking on the turbine icon.
            </p>
            <MapContainer2>
              <MapContainer
                center={defaultCenter}
                zoom={8}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {turbines.map((turbine) => (
                  <Marker key={turbine.id} position={[turbine.location.lat, turbine.location.lng]}>
                    <Popup>
                      <strong>{turbine.name}</strong><br />
                      {turbine.model}<br />
                      Turbine #{turbine.turbineNumber}
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </MapContainer2>
          </Section>
        </>
      ) : (
        <Section>
          <EmptyState>
            <h2>No turbines yet</h2>
            <p>Upload inspection data to add turbines</p>
            <AddButton onClick={handleAddTurbine} style={{ margin: '20px auto 0' }}>
              <Plus />
              Add Turbine
            </AddButton>
          </EmptyState>
        </Section>
      )}
    </PageContainer>
  );
};

export default Turbines;
